// src/pages/Dashboard.js

import React, {
    useEffect,
    useMemo,
    useState
} from 'react';

import axios from 'axios';

const endpoints = {
    users:
        'https://oppiesfunc.azurewebsites.net/api/getUserCount?code=UdEbMZJnsWYD-dWMA0f52I50CqTcIqELKkjhNvHRXnSRAzFup3a_jw==',

    shops:
        'https://oppiesfunc.azurewebsites.net/api/getShopStats?code=gJSEW3ZidtYf9z0synwwOKx4S-AbO-ADvhRabkgux4BzAzFuXTHwaA==',

    reviews:
        'https://oppiesfunc.azurewebsites.net/api/getReviewCount?code=ArosVq4RDoHqMbYvA1ZRuQP5N8P1K6vZO-D36zcKmqZ2AzFuOaxulg==',

    stores:
        'https://oppiesfunc.azurewebsites.net/api/getStoreStats?code=NPR9Sx-ljJ822v-gDalT-UhRnByvBi0SpplFEA-PfQ8RAzFuLE0zjg==',

    community:
        'https://oppiesfunc.azurewebsites.net/api/getCommunityStats?code=O8d9g8XXcVTAxVlBMR9Ka9tndvAJENkSNBh4Hnv8tdp9AzFunj30ug==',
};

const OPPIES_BEIGE = '#bfa97c';
const CARD_BG = '#ffffff';
const BG = '#fffaf1';

function Dashboard() {
    const [stats, setStats] =
        useState({
            /* =============================================
               CORE
               ============================================= */

            totalUsers: null,
            totalShops: null,
            totalStores: null,

            /* =============================================
               SHOP COUNTRIES
               ============================================= */

            newZealandShops: null,
            australiaShops: null,
            uncategorisedShops: null,

            /* =============================================
               APPROVALS
               ============================================= */

            pendingApprovalShops: null,
            pendingApprovalStores: null,

            /* =============================================
               CONTENT
               ============================================= */

            totalReviews: null,
            totalPosts: null,
            totalComments: null,
            totalImages: null,

            /* =============================================
               STORE SUBSCRIPTION HEALTH
               ============================================= */

            storesSubscribedActive: null,
            storesStripeTrialing: null,
            storesPastDue: null,
            storesCanceled: null,
            storesNoSubscription: null,

            /* =============================================
               STORE SUBSCRIPTION REVENUE
               ============================================= */

            monthlyRecurringRevenue: null,
            revenueThisMonth: null,
            lifetimeRevenue: null,
            subscriptionCurrency: 'NZD',

            /* =============================================
               STRIPE
               ============================================= */

            stripeConnected: null,
            stripeWarning: null,
        });

    const [loading, setLoading] =
        useState(true);

    const [
        statusMsg,
        setStatusMsg
    ] =
        useState('');

    const [
        lastUpdated,
        setLastUpdated
    ] =
        useState(null);

    /* =====================================================
       SAFE GET
       ===================================================== */

    const safeGet =
        async (
            url,
            name
        ) => {
            if (!url) {
                return {
                    ok: false,
                    data: null,
                    skipped: true,
                };
            }

            try {
                const separator =
                    url.includes('?')
                        ? '&'
                        : '?';

                const response =
                    await axios.get(
                        `${url}${separator}_=${Date.now()}`,
                        {
                            timeout: 30000,
                        }
                    );

                return {
                    ok: true,
                    data:
                        response?.data ??
                        null,
                };
            } catch (error) {
                console.error(
                    `[Dashboard] ${name} failed:`,
                    error?.response?.data ||
                    error?.message ||
                    error
                );

                return {
                    ok: false,
                    data: null,
                    error,
                };
            }
        };

    /* =====================================================
       HELPERS
       ===================================================== */

    const parseNumber =
        (value) => {
            const number =
                Number(value);

            return Number.isFinite(
                number
            )
                ? number
                : null;
        };

    const coalesce =
        (...values) => {
            for (
                const value
                of values
            ) {
                if (
                    value !==
                    undefined &&
                    value !== null
                ) {
                    return value;
                }
            }

            return null;
        };

    /* =====================================================
       LOAD DASHBOARD
       ===================================================== */

    const fetchStats =
        async () => {
            setLoading(true);
            setStatusMsg('');

            const [
                usersResponse,
                shopsResponse,
                reviewsResponse,
                storesResponse,
                communityResponse,
            ] =
                await Promise.all([
                    safeGet(
                        endpoints.users,
                        'users'
                    ),

                    safeGet(
                        endpoints.shops,
                        'shops'
                    ),

                    safeGet(
                        endpoints.reviews,
                        'reviews'
                    ),

                    safeGet(
                        endpoints.stores,
                        'stores'
                    ),

                    safeGet(
                        endpoints.community,
                        'community'
                    ),
                ]);

            /* =============================================
               USERS
               ============================================= */

            const totalUsers =
                parseNumber(
                    coalesce(
                        usersResponse?.data
                            ?.totalUsers,

                        usersResponse?.data
                            ?.count,

                        usersResponse?.data
                            ?.total,

                        0
                    )
                );

            /* =============================================
               SHOPS
               ============================================= */

            const totalShops =
                parseNumber(
                    coalesce(
                        shopsResponse?.data
                            ?.totalShops,

                        shopsResponse?.data
                            ?.count,

                        shopsResponse?.data
                            ?.total,

                        0
                    )
                );

            const newZealandShops =
                parseNumber(
                    coalesce(
                        shopsResponse?.data
                            ?.newZealandShops,

                        shopsResponse?.data
                            ?.nzShops,

                        0
                    )
                );

            const australiaShops =
                parseNumber(
                    coalesce(
                        shopsResponse?.data
                            ?.australiaShops,

                        shopsResponse?.data
                            ?.auShops,

                        0
                    )
                );

            const uncategorisedShops =
                parseNumber(
                    coalesce(
                        shopsResponse?.data
                            ?.uncategorisedShops,

                        shopsResponse?.data
                            ?.unknownCountryShops,

                        0
                    )
                );

            const pendingApprovalShops =
                parseNumber(
                    coalesce(
                        shopsResponse?.data
                            ?.pendingApproval,

                        shopsResponse?.data
                            ?.pending,

                        shopsResponse?.data
                            ?.pendingApprovals,

                        0
                    )
                );

            /* =============================================
               REVIEWS
               ============================================= */

            const totalReviews =
                parseNumber(
                    coalesce(
                        reviewsResponse?.data
                            ?.totalReviews,

                        reviewsResponse?.data
                            ?.count,

                        reviewsResponse?.data
                            ?.total,

                        0
                    )
                );

            /* =============================================
               STORES
               ============================================= */

            const totalStores =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.totalStores,

                            storesResponse
                                ?.data
                                ?.count,

                            storesResponse
                                ?.data
                                ?.total,

                            0
                        )
                    )
                    : null;

            const pendingApprovalStores =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.pendingApproval,

                            storesResponse
                                ?.data
                                ?.pending,

                            storesResponse
                                ?.data
                                ?.pendingApprovals,

                            0
                        )
                    )
                    : null;

            /* =============================================
               COMMUNITY
               ============================================= */

            const totalPosts =
                communityResponse?.ok
                    ? parseNumber(
                        coalesce(
                            communityResponse
                                ?.data
                                ?.totalPosts,

                            0
                        )
                    )
                    : null;

            const totalComments =
                communityResponse?.ok
                    ? parseNumber(
                        coalesce(
                            communityResponse
                                ?.data
                                ?.totalComments,

                            0
                        )
                    )
                    : null;

            const totalImages =
                communityResponse?.ok
                    ? parseNumber(
                        coalesce(
                            communityResponse
                                ?.data
                                ?.totalImages,

                            0
                        )
                    )
                    : null;

            /* =============================================
               STORE SUBSCRIPTION HEALTH
               ============================================= */

            const storesSubscribedActive =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.activeSubscriptions,

                            storesResponse
                                ?.data
                                ?.subscribedActive,

                            storesResponse
                                ?.data
                                ?.subscriptionActive,

                            storesResponse
                                ?.data
                                ?.active,

                            0
                        )
                    )
                    : null;

            const storesStripeTrialing =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.trialSubscriptions,

                            storesResponse
                                ?.data
                                ?.stripeTrialing,

                            storesResponse
                                ?.data
                                ?.trialing,

                            0
                        )
                    )
                    : null;

            const storesPastDue =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.overdueSubscriptions,

                            storesResponse
                                ?.data
                                ?.pastDue,

                            storesResponse
                                ?.data
                                ?.stripePastDue,

                            0
                        )
                    )
                    : null;

            const storesCanceled =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.expiredSubscriptions,

                            storesResponse
                                ?.data
                                ?.canceled,

                            storesResponse
                                ?.data
                                ?.stripeCanceled,

                            0
                        )
                    )
                    : null;

            const storesNoSubscription =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.noSubscription,

                            storesResponse
                                ?.data
                                ?.withoutSubscription,

                            0
                        )
                    )
                    : null;

            /* =============================================
               STORE REVENUE
               ============================================= */

            const monthlyRecurringRevenue =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.monthlyRecurringRevenue,

                            storesResponse
                                ?.data
                                ?.mrr,

                            0
                        )
                    )
                    : null;

            const revenueThisMonth =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.revenueThisMonth,

                            storesResponse
                                ?.data
                                ?.monthlyRevenue,

                            0
                        )
                    )
                    : null;

            const lifetimeRevenue =
                storesResponse?.ok
                    ? parseNumber(
                        coalesce(
                            storesResponse
                                ?.data
                                ?.lifetimeRevenue,

                            storesResponse
                                ?.data
                                ?.totalRevenue,

                            0
                        )
                    )
                    : null;

            const subscriptionCurrency =
                storesResponse?.data
                    ?.currency ||
                'NZD';

            const stripeConnected =
                storesResponse?.ok
                    ? Boolean(
                        storesResponse
                            ?.data
                            ?.stripeConnected
                    )
                    : null;

            const stripeWarning =
                storesResponse?.data
                    ?.stripeWarning ||
                null;

            /* =============================================
               UPDATE STATE
               ============================================= */

            const next = {
                totalUsers,
                totalShops,
                totalStores,

                newZealandShops,
                australiaShops,
                uncategorisedShops,

                pendingApprovalShops,
                pendingApprovalStores,

                totalReviews,
                totalPosts,
                totalComments,
                totalImages,

                storesSubscribedActive,
                storesStripeTrialing,
                storesPastDue,
                storesCanceled,
                storesNoSubscription,

                monthlyRecurringRevenue,
                revenueThisMonth,
                lifetimeRevenue,
                subscriptionCurrency,

                stripeConnected,
                stripeWarning,
            };

            /* =============================================
               HEALTH CHECK
               ============================================= */

            const essentialsOk =
                usersResponse.ok &&
                shopsResponse.ok &&
                reviewsResponse.ok;

            const essentialsZero =
                [
                    totalUsers,
                    totalShops,
                    totalReviews,
                ].every(
                    (number) =>
                        typeof number ===
                        'number' &&
                        number === 0
                );

            if (
                essentialsOk &&
                essentialsZero
            ) {
                setStatusMsg(
                    'All core counts are 0. Check Azure Function responses in DevTools → Network for a query or container mismatch.'
                );
            } else if (
                !essentialsOk
            ) {
                setStatusMsg(
                    'One or more core stat endpoints failed. Check that the Azure Functions are live and their keys are correct.'
                );
            } else if (
                !storesResponse.ok
            ) {
                setStatusMsg(
                    'Store subscription statistics could not be loaded. Check the getStoreStats Azure Function.'
                );
            } else {
                setStatusMsg('');
            }

            setStats(next);
            setLastUpdated(
                new Date()
            );
            setLoading(false);
        };

    /* =====================================================
       INITIAL LOAD
       ===================================================== */

    useEffect(() => {
        fetchStats();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* =====================================================
       DASHBOARD SECTIONS
       ===================================================== */

    const sections =
        useMemo(() => {
            return [
                /* =========================================
                   HIGH LEVEL
                   ========================================= */

                {
                    title:
                        'High-level',

                    subtitle:
                        'Core platform totals',

                    cards: [
                        {
                            label:
                                'Total Users',

                            value:
                                stats.totalUsers,

                            tone:
                                'neutral',
                        },

                        {
                            label:
                                'Total Shops',

                            value:
                                stats.totalShops,

                            tone:
                                'neutral',
                        },

                        {
                            label:
                                'Total Stores',

                            value:
                                stats.totalStores,

                            tone:
                                'neutral',
                        },
                    ],
                },

                /* =========================================
                   SHOP COVERAGE
                   ========================================= */

                {
                    title:
                        'Op Shop coverage',

                    subtitle:
                        'Shops by country',

                    hint:
                        stats.uncategorisedShops > 0
                            ? `${stats.uncategorisedShops} shop${stats.uncategorisedShops === 1 ? '' : 's'} still need a country assigned.`
                            : 'All Op Shops have a country assigned.',

                    cards: [
                        {
                            label:
                                'New Zealand',

                            value:
                                stats.newZealandShops,

                            tone:
                                'good',
                        },

                        {
                            label:
                                'Australia',

                            value:
                                stats.australiaShops,

                            tone:
                                'good',
                        },

                        {
                            label:
                                'Uncategorised',

                            value:
                                stats.uncategorisedShops,

                            tone:
                                stats.uncategorisedShops > 0
                                    ? 'warn'
                                    : 'neutral',
                        },
                    ],
                },

                /* =========================================
                   APPROVALS
                   ========================================= */

                {
                    title:
                        'Approvals',

                    subtitle:
                        'Queues that need attention',

                    cards: [
                        {
                            label:
                                'Pending Shop Approvals',

                            value:
                                stats.pendingApprovalShops,

                            tone:
                                'warn',
                        },

                        {
                            label:
                                'Pending Store Approvals',

                            value:
                                stats.pendingApprovalStores,

                            tone:
                                'warn',
                        },
                    ],
                },

                /* =========================================
                   COMMUNITY
                   ========================================= */

                {
                    title:
                        'Community',

                    subtitle:
                        'Feed and engagement',

                    cards: [
                        {
                            label:
                                'Total Posts',

                            value:
                                stats.totalPosts,

                            tone:
                                'neutral',
                        },

                        {
                            label:
                                'Total Comments',

                            value:
                                stats.totalComments,

                            tone:
                                'neutral',
                        },

                        {
                            label:
                                'Total Images',

                            value:
                                stats.totalImages,

                            tone:
                                'neutral',
                        },

                        {
                            label:
                                'Total Reviews',

                            value:
                                stats.totalReviews,

                            tone:
                                'neutral',
                        },
                    ],
                },

                /* =========================================
                   STORE SUBSCRIPTIONS
                   ========================================= */

                {
                    title:
                        'Store subscriptions',

                    subtitle:
                        'Current subscription health',

                    hint:
                        stats.stripeWarning ||
                        'Subscription counts are based on the latest Store and Stripe status values.',

                    cards: [
                        {
                            label:
                                'Active Subscriptions',

                            value:
                                stats.storesSubscribedActive,

                            tone:
                                'good',
                        },

                        {
                            label:
                                'Trialing',

                            value:
                                stats.storesStripeTrialing,

                            tone:
                                'neutral',
                        },

                        {
                            label:
                                'Past Due / Overdue',

                            value:
                                stats.storesPastDue,

                            tone:
                                'bad',
                        },

                        {
                            label:
                                'Canceled / Expired',

                            value:
                                stats.storesCanceled,

                            tone:
                                'bad',
                        },

                        {
                            label:
                                'No Subscription',

                            value:
                                stats.storesNoSubscription,

                            tone:
                                'warn',
                        },
                    ],
                },

                /* =========================================
                   STORE REVENUE
                   ========================================= */

                {
                    title:
                        'Store subscription revenue',

                    subtitle:
                        'Gross Stripe subscription income',

                    hint:
                        stats.stripeConnected ===
                            false
                            ? 'Stripe is not connected. Check STRIPE_SECRET_KEY in the Azure Function App settings.'
                            : 'MRR is recurring subscription value. Revenue figures reflect paid Stripe invoices for OPPIES Store customers.',

                    cards: [
                        {
                            label:
                                'Monthly Recurring Revenue',

                            value:
                                stats.monthlyRecurringRevenue,

                            tone:
                                'good',

                            format:
                                'currency',

                            currency:
                                stats.subscriptionCurrency,
                        },

                        {
                            label:
                                'Revenue This Month',

                            value:
                                stats.revenueThisMonth,

                            tone:
                                'good',

                            format:
                                'currency',

                            currency:
                                stats.subscriptionCurrency,
                        },

                        {
                            label:
                                'Lifetime Revenue',

                            value:
                                stats.lifetimeRevenue,

                            tone:
                                'neutral',

                            format:
                                'currency',

                            currency:
                                stats.subscriptionCurrency,
                        },
                    ],
                },
            ];
        }, [stats]);

    /* =====================================================
       RENDER
       ===================================================== */

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div
                    style={
                        styles.headerRow
                    }
                >
                    <div>
                        <h2
                            style={
                                styles.title
                            }
                        >
                            Admin Dashboard
                        </h2>

                        <div
                            style={
                                styles.subTitle
                            }
                        >
                            {lastUpdated ? (
                                <span>
                                    Last updated:{' '}
                                    <strong>
                                        {formatDateTime(
                                            lastUpdated
                                        )}
                                    </strong>
                                </span>
                            ) : (
                                <span>
                                    Loading…
                                </span>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={
                            fetchStats
                        }
                        disabled={
                            loading
                        }
                        style={{
                            ...styles.refreshBtn,

                            opacity:
                                loading
                                    ? 0.7
                                    : 1,

                            cursor:
                                loading
                                    ? 'not-allowed'
                                    : 'pointer',
                        }}
                        title="Refresh dashboard"
                    >
                        {loading
                            ? 'Refreshing…'
                            : 'Refresh'}
                    </button>
                </div>

                {statusMsg && (
                    <div
                        style={
                            styles.notice
                        }
                    >
                        {statusMsg}
                    </div>
                )}

                {loading ? (
                    <p
                        style={
                            styles.loadingText
                        }
                    >
                        Loading dashboard…
                    </p>
                ) : (
                    <div
                        style={
                            styles.sections
                        }
                    >
                        {sections.map(
                            (section) => (
                                <div
                                    key={
                                        section.title
                                    }
                                    style={
                                        styles.sectionCard
                                    }
                                >
                                    <div
                                        style={
                                            styles.sectionHead
                                        }
                                    >
                                        <div>
                                            <div
                                                style={
                                                    styles.sectionTitle
                                                }
                                            >
                                                {
                                                    section.title
                                                }
                                            </div>

                                            <div
                                                style={
                                                    styles.sectionSubtitle
                                                }
                                            >
                                                {
                                                    section.subtitle
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    {section.hint ? (
                                        <div
                                            style={
                                                styles.hint
                                            }
                                        >
                                            {
                                                section.hint
                                            }
                                        </div>
                                    ) : null}

                                    <div
                                        style={
                                            styles.grid
                                        }
                                    >
                                        {section.cards.map(
                                            (
                                                card
                                            ) => (
                                                <StatCard
                                                    key={
                                                        card.label
                                                    }

                                                    label={
                                                        card.label
                                                    }

                                                    value={
                                                        card.value
                                                    }

                                                    tone={
                                                        card.tone
                                                    }

                                                    format={
                                                        card.format
                                                    }

                                                    currency={
                                                        card.currency
                                                    }
                                                />
                                            )
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/* =========================================================
   STAT CARD
   ========================================================= */

function StatCard({
    label,
    value,
    tone = 'neutral',
    format = 'number',
    currency = 'NZD',
}) {
    const showValue =
        () => {
            if (
                value === null ||
                value === undefined
            ) {
                return '—';
            }

            const number =
                Number(value);

            if (
                !Number.isFinite(
                    number
                )
            ) {
                return String(
                    value
                );
            }

            if (
                format ===
                'currency'
            ) {
                try {
                    return new Intl.NumberFormat(
                        'en-NZ',
                        {
                            style:
                                'currency',

                            currency,

                            minimumFractionDigits:
                                2,

                            maximumFractionDigits:
                                2,
                        }
                    ).format(
                        number
                    );
                } catch {
                    return (
                        `NZ$${number.toLocaleString(
                            'en-NZ',
                            {
                                minimumFractionDigits:
                                    2,

                                maximumFractionDigits:
                                    2,
                            }
                        )}`
                    );
                }
            }

            return number.toLocaleString(
                'en-NZ'
            );
        };

    const toneStyle =
        tone === 'good'
            ? styles.toneGood
            : tone === 'warn'
                ? styles.toneWarn
                : tone === 'bad'
                    ? styles.toneBad
                    : styles.toneNeutral;

    return (
        <div
            style={{
                ...styles.card,
                ...toneStyle,
            }}
        >
            <div
                style={
                    styles.cardLabel
                }
            >
                {label}
            </div>

            <div
                style={
                    styles.cardValue
                }
            >
                {showValue()}
            </div>
        </div>
    );
}

/* =========================================================
   DATE
   ========================================================= */

function formatDateTime(
    date
) {
    try {
        return date.toLocaleString(
            'en-NZ'
        );
    } catch {
        return String(
            date
        );
    }
}

/* =========================================================
   STYLES
   ========================================================= */

const styles = {
    page: {
        background:
            BG,

        minHeight:
            '100vh',
    },

    container: {
        padding:
            '26px',

        maxWidth:
            1120,

        margin:
            '0 auto',
    },

    headerRow: {
        display:
            'flex',

        alignItems:
            'flex-start',

        justifyContent:
            'space-between',

        gap:
            12,

        marginBottom:
            14,
    },

    title: {
        margin:
            0,

        fontSize:
            26,

        color:
            '#333',

        letterSpacing:
            0.2,
    },

    subTitle: {
        marginTop:
            6,

        fontSize:
            12,

        color:
            '#6b5b3a',

        opacity:
            0.9,
    },

    refreshBtn: {
        border:
            `1px solid ${OPPIES_BEIGE}`,

        background:
            '#fff',

        color:
            '#6b5b3a',

        padding:
            '9px 12px',

        borderRadius:
            10,

        fontWeight:
            700,

        boxShadow:
            '0 2px 6px rgba(0,0,0,0.06)',

        height:
            40,
    },

    notice: {
        background:
            '#fff9e6',

        border:
            '1px solid #ffe199',

        color:
            '#6b5b3a',

        borderRadius:
            10,

        padding:
            '10px 12px',

        marginBottom:
            16,

        textAlign:
            'center',
    },

    loadingText: {
        textAlign:
            'center',

        color:
            '#6b5b3a',
    },

    sections: {
        display:
            'grid',

        gridTemplateColumns:
            '1fr',

        gap:
            16,
    },

    sectionCard: {
        background:
            CARD_BG,

        borderRadius:
            14,

        border:
            '1px solid rgba(191,169,124,0.25)',

        boxShadow:
            '0 6px 16px rgba(0,0,0,0.06)',

        padding:
            16,
    },

    sectionHead: {
        display:
            'flex',

        alignItems:
            'center',

        justifyContent:
            'space-between',

        gap:
            10,

        marginBottom:
            10,
    },

    sectionTitle: {
        fontSize:
            14,

        fontWeight:
            800,

        color:
            '#333',
    },

    sectionSubtitle: {
        fontSize:
            12,

        color:
            '#6b5b3a',

        opacity:
            0.85,

        marginTop:
            2,
    },

    hint: {
        marginTop:
            6,

        marginBottom:
            12,

        fontSize:
            12,

        color:
            '#6b5b3a',

        opacity:
            0.85,

        background:
            '#fff9f1',

        border:
            '1px dashed rgba(191,169,124,0.65)',

        borderRadius:
            10,

        padding:
            '10px 12px',
    },

    grid: {
        display:
            'grid',

        gridTemplateColumns:
            'repeat(auto-fit, minmax(220px, 1fr))',

        gap:
            14,
    },

    card: {
        backgroundColor:
            '#ffffff',

        borderRadius:
            12,

        padding:
            '14px 14px',

        textAlign:
            'left',

        border:
            '1px solid rgba(0,0,0,0.05)',

        minHeight:
            94,
    },

    cardLabel: {
        fontSize:
            12,

        color:
            '#6b5b3a',

        opacity:
            0.85,

        fontWeight:
            700,
    },

    cardValue: {
        fontSize:
            28,

        marginTop:
            10,

        color:
            '#333',

        fontWeight:
            900,

        letterSpacing:
            0.2,

        overflowWrap:
            'anywhere',
    },

    toneNeutral: {},

    toneGood: {
        border:
            '1px solid rgba(66,169,107,0.35)',

        background:
            'linear-gradient(180deg, #ffffff 0%, #f6fff9 100%)',
    },

    toneWarn: {
        border:
            '1px solid rgba(191,169,124,0.55)',

        background:
            'linear-gradient(180deg, #ffffff 0%, #fff9f1 100%)',
    },

    toneBad: {
        border:
            '1px solid rgba(169,68,66,0.25)',

        background:
            'linear-gradient(180deg, #ffffff 0%, #fff5f5 100%)',
    },
};

export default Dashboard;